import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { doInitLU, luHandleAppiError, type U3204InitLU } from './U3204Service';
import { U3204ContextProvider } from './U3204Context';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

export const NS = 'u3204';

export const U3204ROLES = {
  READ: 'U3204:READ',
  ADD: 'U3204:ADD',
  ADMIN: 'U3204:ADMIN',
} as const;
export type U3204ROLES = (typeof U3204ROLES)[keyof typeof U3204ROLES];

const defaultInitLU: U3204InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 50,
  logicalUnitCode: 'U3204',
  allowedMimeTypes: ['text/plain', 'application/pdf', 'image/png', 'image/jpeg', 'image/webp'],
  maxAttachmentBytes: 10485760,
  maxAttachmentsPerMessage: 5,
  messageRoles: ['user', 'assistant', 'system'],
  showThinking: false,
  hasUserGroup: false,
  userGroup: null,
};

const U3204: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3204InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(['u3204'], (err) => {
      if (err) return console.error('Error loading namespace:', err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3204InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3204InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        logicalUnitCode: initLU.logicalUnitCode ?? 'U3204',
        allowedMimeTypes: initLU.allowedMimeTypes ?? defaultInitLU.allowedMimeTypes,
        maxAttachmentBytes: initLU.maxAttachmentBytes ?? defaultInitLU.maxAttachmentBytes,
        maxAttachmentsPerMessage: initLU.maxAttachmentsPerMessage ?? defaultInitLU.maxAttachmentsPerMessage,
        messageRoles: initLU.messageRoles ?? defaultInitLU.messageRoles,
        showThinking: initLU.showThinking ?? defaultInitLU.showThinking,
        hasUserGroup: initLU.hasUserGroup ?? false,
        userGroup: initLU.userGroup ?? null,
      });
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t('title', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, setLogicalUnitTitle, setLogicalUnitBreadcrumb, t]);

  if (!i18nReady || !initLUReady) {
    return null;
  }

  return (
    <U3204ContextProvider initLU={initLUData}>
      <div className="flex h-full max-h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
        <Outlet />
      </div>
    </U3204ContextProvider>
  );
};

export default U3204;
